import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface CustomerVerificationDialogProps {
  open: boolean;
  customerEmail: string;
  verificationToken: string;
  onVerified: () => void;
  onCancel: () => void;
}

export const CustomerVerificationDialog = ({
  open,
  customerEmail,
  verificationToken,
  onVerified,
  onCancel,
}: CustomerVerificationDialogProps) => {
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (!open || !verificationToken) return;

    // Poll for verification status - check if customer_id was set in d2d_unit
    const checkVerification = async () => {
      const { data, error } = await supabase
        .from("d2d_units")
        .select("customer_id")
        .eq("verification_token", verificationToken)
        .single();

      if (!error && data?.customer_id) {
        setIsVerified(true);
        setTimeout(() => {
          onVerified();
        }, 2000);
      }
    };

    checkVerification();
    const interval = setInterval(checkVerification, 3000);

    return () => clearInterval(interval);
  }, [open, verificationToken, onVerified]);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            {isVerified ? "Bestätigung erfolgreich!" : "Warte auf Kundenbestätigung"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center justify-center py-6 space-y-4">
          {!isVerified ? (
            <>
              <div className="relative">
                <Mail className="w-16 h-16 text-primary" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full animate-ping" />
              </div>
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <div className="text-center space-y-2">
                <p className="font-medium">
                  Bestätigungs-E-Mail wurde versendet an:
                </p>
                <p className="text-sm text-muted-foreground font-mono bg-muted px-3 py-1 rounded">
                  {customerEmail}
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Bitte warten Sie, bis der Kunde seine Zustimmung erteilt hat.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <div className="text-center space-y-2">
                <p className="font-medium text-green-600">
                  Kunde hat bestätigt!
                </p>
                <p className="text-sm text-muted-foreground">
                  Der Kunde wird jetzt angelegt...
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
